import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/EmptyState';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import { fmtDate, fmtInt } from '@/lib/format';
import {
  statusLabel,
  useFormPackages,
  type FormPackageRow,
  type FormPackageStatus,
} from '@/lib/forms';

const STATUSES: Array<{ value: '' | FormPackageStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafting' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'ready', label: 'Ready' },
  { value: 'packaged', label: 'Packaged' },
];

export default function Forms() {
  const [status, setStatus] = useState<'' | FormPackageStatus>('');
  const { data, isLoading, isError, error } = useFormPackages({
    status: status || undefined,
    limit: 50,
  });

  const packages = data?.packages ?? [];
  const reviewTotal = packages.reduce(
    (acc, p) => acc + (p.fields_needs_review ?? 0),
    0
  );

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Submission"
        title="Forms"
        dateline={
          isLoading
            ? 'Loading…'
            : `${fmtInt(packages.length)} package${packages.length === 1 ? '' : 's'} · ${fmtInt(reviewTotal)} field${reviewTotal === 1 ? '' : 's'} awaiting review`
        }
        lede="Submission-ready GSA fill packages. The agent pre-fills fields it is confident about, flags the rest for you, and only renders the final PDF after a human signs off on every override."
      />

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="flex items-center gap-1 rounded-lg border border-rule bg-surface-sunk p-1">
          {STATUSES.map((s) => (
            <button
              key={s.value || 'all'}
              type="button"
              onClick={() => setStatus(s.value)}
              className={
                'rounded-md px-3 py-1.5 text-[0.8125rem] font-semibold transition-all ' +
                (status === s.value
                  ? 'bg-surface text-ink shadow-card'
                  : 'text-ink-muted hover:text-ink')
              }
              data-testid={`forms-status-${s.value || 'all'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Failed to load packages: {error instanceof Error ? error.message : 'unknown'}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card flex flex-col gap-3 p-6">
              <SkeletonLine width="60%" />
              <SkeletonLine width="40%" />
              <SkeletonLine width="80%" />
            </div>
          ))}
        </div>
      ) : packages.length === 0 ? (
        <EmptyState
          title="No form packages yet"
          description="Open an RLP, review candidate properties, then tap Start form fill on the one you want to submit."
        />
      ) : (
        <div className="flex flex-col gap-4" data-testid="forms-list">
          {packages.map((p) => (
            <PackageCard key={p.id} pkg={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg }: { pkg: FormPackageRow }) {
  const review = pkg.fields_needs_review ?? 0;
  const total = pkg.fields_total ?? 0;
  const confidence = pkg.ai_confidence != null ? Math.round(pkg.ai_confidence * 100) : null;

  return (
    <article
      className="group relative overflow-hidden rounded-xl border border-rule bg-surface shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover"
      data-testid={`forms-row-${pkg.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px]">
        <div className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1.5">
            <Link
              to={`/forms/${pkg.id}`}
              className="text-[1.0625rem] font-bold leading-snug tracking-head text-brand-700 hover:text-brand-800 hover:underline"
            >
              {pkg.legal_entity} → {pkg.candidate_address}
            </Link>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.8125rem]">
              <span className="font-mono text-ink">
                <span className="text-ink-muted">RLP:</span>{' '}
                <span className="font-semibold">{pkg.solicitation_number ?? '—'}</span>
              </span>
              {pkg.agency && <span className="text-ink-muted">{pkg.agency}</span>}
              <StatusBadge status={pkg.status} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 border-t border-rule pt-4">
            <Chip icon={ClipboardList} tone="neutral" title="Total fields">
              {fmtInt(total)} field{total === 1 ? '' : 's'}
            </Chip>
            {review > 0 ? (
              <Chip icon={Sparkles} tone="warn" title="Fields awaiting your review">
                {fmtInt(review)} to review
              </Chip>
            ) : (
              <Chip icon={CheckCircle2} tone="success">
                All fields reviewed
              </Chip>
            )}
            {confidence != null && (
              <Chip icon={Building2} tone="info" title="AI confidence across proposed fields">
                {confidence}% confidence
              </Chip>
            )}
            {pkg.packaged_at != null && (
              <Chip icon={FileCheck2} tone="success" title="Packaged timestamp">
                Packaged {fmtDate(pkg.packaged_at)}
              </Chip>
            )}
            <Link
              to={`/forms/${pkg.id}`}
              className="ml-auto inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-brand-700 opacity-0 transition-opacity group-hover:opacity-100 hover:text-brand-800"
            >
              Review <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <aside className="flex flex-col gap-3 border-t border-rule bg-surface-sunk p-6 md:border-l md:border-t-0">
          <div>
            <div className="text-[0.6875rem] font-medium text-ink-muted">Updated</div>
            <div className="text-[0.8125rem] font-semibold text-ink">
              {fmtDate(pkg.updated_at)}
            </div>
          </div>
          <div>
            <div className="text-[0.6875rem] font-medium text-ink-muted">Submission form</div>
            <div className="text-[0.8125rem] font-semibold text-ink">GSA 527 · V1</div>
          </div>
          {pkg.status === 'packaged' && pkg.packaged_r2_key && (
            <a
              href={`/api/forms/${pkg.id}/download`}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-[0.8125rem] font-semibold text-brand-800 hover:bg-brand-100"
            >
              <FileCheck2 className="h-3.5 w-3.5" />
              Download PDF
            </a>
          )}
        </aside>
      </div>
    </article>
  );
}

function StatusBadge({ status }: { status: FormPackageStatus }) {
  const tone =
    status === 'packaged' || status === 'delivered'
      ? 'green'
      : status === 'ready'
        ? 'blue'
        : status === 'needs_review'
          ? 'yellow'
          : 'neutral';
  return (
    <Badge tone={tone} dot>
      {statusLabel(status)}
    </Badge>
  );
}

function Chip({
  icon: Icon,
  tone = 'neutral',
  children,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'neutral' | 'warn' | 'info' | 'success';
  children: React.ReactNode;
  title?: string;
}) {
  const cls =
    tone === 'warn'
      ? 'bg-warn-soft text-warn-bold ring-warn-border'
      : tone === 'info'
        ? 'bg-info-soft text-info-bold ring-info-border'
        : tone === 'success'
          ? 'bg-success-soft text-success-bold ring-success-border'
          : 'bg-surface-sunk text-ink-muted ring-rule';
  return (
    <span
      title={title}
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset ' +
        cls
      }
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

// Kept here so we can swap the loader while any mutation is pending without
// re-rendering the whole list. Not used in the V1 layout above but exported
// for the detail page to reuse.
export function InlineSpinner() {
  return <Loader2 className="h-4 w-4 animate-spin text-brand-600" />;
}
